import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera, AlertCircle } from 'lucide-react';

/**
 * Componente para escanear códigos QR usando la cámara
 * @param {Object} props
 * @param {boolean} props.isOpen - Si el modal está abierto
 * @param {Function} props.onClose - Función para cerrar el modal
 * @param {Function} props.onSuccess - Función callback cuando se escanea exitosamente (recibe equipoId)
 */
const QRScanner = ({ isOpen, onClose, onSuccess }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [cameraPermission, setCameraPermission] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  /**
   * Iniciar el escáner de QR
   */
  const startScanner = async () => {
    try {
      setError(null);
      setScanning(true);

      // Verificar que estamos en contexto seguro (HTTPS o localhost)
      const isSecureContext = window.isSecureContext;
      console.log('Es contexto seguro (HTTPS):', isSecureContext);
      console.log('URL actual:', window.location.href);
      console.log('Protocol:', window.location.protocol);

      if (!isSecureContext && !window.location.hostname.includes('localhost')) {
        setError('⚠️ El acceso a la cámara requiere conexión HTTPS. Este sitio está usando HTTP (no seguro). Por favor, contacta al administrador del sistema para habilitar HTTPS.');
        setScanning(false);
        return;
      }

      // Verificar que navigator.mediaDevices existe
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('❌ Tu navegador no soporta acceso a la cámara. Intenta actualizar tu navegador o usa Chrome/Firefox/Safari.');
        setScanning(false);
        return;
      }

      // Verificar permisos antes de continuar
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'camera' });
        console.log('Estado del permiso de cámara:', permissionStatus.state);
        
        if (permissionStatus.state === 'denied') {
          setError('❌ Permisos de cámara denegados previamente. Ve a la configuración de tu navegador → Permisos del sitio → Cámara y permite el acceso.');
          setCameraPermission('denied');
          setScanning(false);
          return;
        }
      } catch (permError) {
        console.log('No se pudo verificar permisos (puede ser normal en algunos navegadores):', permError);
      }

      // Verificar si hay cámaras disponibles
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        
        console.log('Dispositivos de video encontrados:', videoDevices.length);
        console.log('Lista de dispositivos:', videoDevices);
        
        if (videoDevices.length === 0) {
          setError('❌ No se encontró ninguna cámara en este dispositivo. El escaneo de QR requiere una cámara.');
          setScanning(false);
          return;
        }
      } catch (enumError) {
        console.error('Error al enumerar dispositivos:', enumError);
        // Continuar de todos modos, puede que funcione
      }

      // Crear instancia del escáner
      html5QrCodeRef.current = new Html5Qrcode('qr-reader');

      // Configuración del escáner
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };

      console.log('Intentando iniciar escáner...');

      // Intentar primero con cámara trasera
      try {
        await html5QrCodeRef.current.start(
          { facingMode: 'environment' },
          config,
          onScanSuccess,
          onScanFailure
        );
        console.log('✅ Escáner iniciado con cámara trasera');
      } catch (backCameraError) {
        console.log('❌ Cámara trasera falló, intentando frontal...', backCameraError);
        
        // Si falla, intentar con cámara frontal
        try {
          await html5QrCodeRef.current.start(
            { facingMode: 'user' },
            config,
            onScanSuccess,
            onScanFailure
          );
          console.log('✅ Escáner iniciado con cámara frontal');
        } catch (frontCameraError) {
          console.log('❌ Cámara frontal también falló, intentando con cualquier cámara...', frontCameraError);
          
          // Último intento: usar cualquier cámara disponible
          await html5QrCodeRef.current.start(
            { video: true },
            config,
            onScanSuccess,
            onScanFailure
          );
          console.log('✅ Escáner iniciado con cualquier cámara');
        }
      }

      setCameraPermission('granted');
    } catch (err) {
      console.error('❌ Error COMPLETO al iniciar escáner:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        error: err
      });
      
      let errorMessage = 'Error desconocido al iniciar la cámara.';
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = '🚫 Permiso de cámara denegado. Debes permitir el acceso a la cámara cuando tu navegador lo solicite. Si no aparece la solicitud, ve a Configuración del navegador → Permisos del sitio → Cámara.';
        setCameraPermission('denied');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError' || err.message?.includes('not found')) {
        errorMessage = '📷 No se encontró ninguna cámara. Esta función solo funciona en dispositivos con cámara (teléfonos, tablets, laptops con webcam).';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = '⚠️ La cámara está siendo usada por otra aplicación. Cierra otras apps que usen la cámara (Instagram, WhatsApp, etc.) e intenta de nuevo.';
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage = '⚙️ No se pudo configurar la cámara. Intenta cerrar y abrir el navegador de nuevo.';
      } else if (err.name === 'TypeError') {
        errorMessage = '🔧 Error de configuración del escáner. Recarga la página (Ctrl+R) e intenta de nuevo.';
      } else if (err.name === 'NotSupportedError' || err.message?.includes('https')) {
        errorMessage = '🔒 El acceso a la cámara solo funciona en sitios HTTPS (seguros). Este sitio está usando HTTP. Contacta al administrador.';
      } else if (err.message) {
        errorMessage = `❌ Error: ${err.message}`;
      }
      
      // Agregar información de contexto
      if (!window.isSecureContext) {
        errorMessage += '\n\n⚠️ IMPORTANTE: Este sitio no es seguro (HTTP). La mayoría de navegadores modernos solo permiten acceso a la cámara en sitios HTTPS.';
      }
      
      setError(errorMessage);
      setScanning(false);
    }
  };

  /**
   * Detener el escáner
   */
  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error('Error al detener escáner:', err);
      }
    }
    setScanning(false);
  };

  /**
   * Callback cuando se escanea exitosamente un QR
   */
  const onScanSuccess = (decodedText, decodedResult) => {
    console.log('QR escaneado:', decodedText);
    
    try {
      // Extraer el ID del equipo de la URL
      // Formato esperado: https://app.elements.com.py/equipos/{equipoId}
      const urlPattern = /\/equipos\/([a-f0-9]{24})/i;
      const match = decodedText.match(urlPattern);
      
      if (match && match[1]) {
        const equipoId = match[1];
        console.log('Equipo ID extraído:', equipoId);
        
        // Detener el escáner antes de navegar
        stopScanner();
        
        // Llamar al callback con el ID del equipo
        if (onSuccess) {
          onSuccess(equipoId);
        }
      } else {
        setError('El código QR escaneado no corresponde a un equipo válido.');
        // Detener el escáner si el QR no es válido
        stopScanner();
      }
    } catch (err) {
      console.error('Error al procesar QR:', err);
      setError('Error al procesar el código QR escaneado.');
      stopScanner();
    }
  };

  /**
   * Callback cuando falla el escaneo (se ejecuta continuamente, no mostrar errores)
   */
  const onScanFailure = (error) => {
    // No hacer nada, esto se ejecuta constantemente mientras busca un QR
    // console.log('Buscando QR...', error);
  };

  /**
   * Efecto para iniciar/detener el escáner cuando se abre/cierra el modal
   */
  useEffect(() => {
    if (isOpen) {
      startScanner();
    } else {
      stopScanner();
    }

    // Cleanup al desmontar
    return () => {
      stopScanner();
    };
  }, [isOpen]);

  /**
   * Manejar el cierre del modal
   */
  const handleClose = () => {
    stopScanner();
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 relative">
        {/* Botón cerrar */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          aria-label="Cerrar"
        >
          <X size={24} />
        </button>

        {/* Título */}
        <div className="flex items-center gap-3 mb-4">
          <Camera size={28} className="text-emerald-500" />
          <h2 className="text-2xl font-bold text-gray-800">
            Escanear Código QR
          </h2>
        </div>

        {/* Mostrar error si existe */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800 font-medium">Error al iniciar cámara</p>
                <p className="text-sm text-red-600 mt-1 whitespace-pre-line">{error}</p>
              </div>
            </div>
            
            {/* Info de diagnóstico */}
            <div className="mt-3 pt-3 border-t border-red-300">
              <p className="text-xs text-red-700 font-medium mb-1">🔍 Información de diagnóstico:</p>
              <div className="text-xs text-red-600 space-y-1">
                <div>• Protocolo: {window.location.protocol === 'https:' ? '✅ HTTPS (Seguro)' : '❌ HTTP (No seguro)'}</div>
                <div>• Navegador: {
                  navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                  navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                  navigator.userAgent.includes('Safari') ? 'Safari' : 
                  navigator.userAgent.includes('Edge') ? 'Edge' : 'Otro'
                }</div>
                <div>• Dispositivo: {navigator.userAgent.includes('Mobile') ? '📱 Móvil' : '💻 Desktop'}</div>
                <div>• mediaDevices: {navigator.mediaDevices ? '✅ Disponible' : '❌ No disponible'}</div>
                <div>• getUserMedia: {navigator.mediaDevices?.getUserMedia ? '✅ Disponible' : '❌ No disponible'}</div>
              </div>
            </div>
            
            {cameraPermission === 'denied' && (
              <div className="mt-3 p-3 bg-red-100 rounded-lg">
                <p className="text-xs text-red-800 font-medium mb-2">💡 Cómo habilitar permisos:</p>
                <ul className="text-xs text-red-700 space-y-1 ml-4 list-disc">
                  <li><strong>Chrome móvil:</strong> Toca el candado/ⓘ junto a la URL → Permisos → Cámara → Permitir</li>
                  <li><strong>Safari iOS:</strong> Ajustes → Safari → Cámara → Permitir</li>
                  <li><strong>Firefox móvil:</strong> Menú → Configuración → Permisos del sitio → Cámara</li>
                </ul>
              </div>
            )}
            
            {!window.isSecureContext && (
              <div className="mt-3 p-3 bg-orange-100 border border-orange-300 rounded-lg">
                <p className="text-xs text-orange-800">
                  <strong>⚠️ PROBLEMA DETECTADO:</strong> Este sitio está usando HTTP (no seguro). 
                  Los navegadores modernos solo permiten acceso a la cámara en sitios HTTPS. 
                  Contacta al administrador del sistema para habilitar HTTPS.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Área del escáner */}
        <div className="mb-4">
          <div 
            id="qr-reader" 
            className="rounded-lg overflow-hidden border-2 border-gray-200"
            style={{ minHeight: '300px' }}
          />
        </div>

        {/* Instrucciones */}
        <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-sm text-emerald-800">
            <strong>📷 Instrucciones:</strong>
          </p>
          <ul className="text-xs text-emerald-700 mt-2 space-y-1 ml-4 list-disc">
            <li>Permite el acceso a la cámara cuando se solicite</li>
            <li>Apunta la cámara hacia el código QR del equipo</li>
            <li>Mantén el código dentro del área marcada</li>
            <li>El escaneo se realizará automáticamente</li>
          </ul>
          {!scanning && !error && (
            <div className="mt-2 pt-2 border-t border-emerald-300">
              <p className="text-xs text-emerald-600">
                <strong>🔧 Info del navegador:</strong> {navigator.userAgent.includes('Mobile') ? 'Móvil' : 'Desktop'} 
                {' - '}
                {navigator.userAgent.includes('Chrome') ? 'Chrome' : 
                 navigator.userAgent.includes('Firefox') ? 'Firefox' : 
                 navigator.userAgent.includes('Safari') ? 'Safari' : 'Otro'}
              </p>
            </div>
          )}
        </div>

        {/* Estado */}
        {scanning && !error && (
          <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
            <div className="animate-pulse flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
              <span className="text-sm font-medium">Escaneando...</span>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex gap-3">
          {!scanning && error && (
            <button
              onClick={startScanner}
              className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium"
            >
              Reintentar
            </button>
          )}
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  , document.body);
};

export default QRScanner;
